import { useQueryClient } from '@tanstack/react-query';
import {
  useMinIoFileStorageServiceGetApiFilesMyFiles,
  useMinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType,
  useMinIoFileStorageServicePostApiFilesUpload,
  useMinIoFileStorageServiceDeleteApiFilesByFilename,
  useMinIoFileStorageServiceGetApiFilesByFilenameDownload,
  useMinIoFileStorageServicePatchApiFilesByFilenameStatus,
  useMinIoFileStorageServicePatchApiFilesBulkStatus,
  useMinIoFileStorageServiceDeleteApiFilesBulk
} from '../api/generated/queries/queries';
import { FileMetadataDTO } from '../api/generated/requests/types.gen';
import { MinIoFileStorageService } from '../api/generated/requests/services.gen';
import { useSnackbar } from 'notistack';

/**
 * Hook do pobierania wszystkich plików użytkownika z cache'owaniem
 * Używa React Query do cache'owania danych
 */
export const useAllFiles = () => {
  return useMinIoFileStorageServiceGetApiFilesMyFiles([], {
    staleTime: 0, // Zawsze wykonuj request w tle, ale serwuj dane z cache od razu
    gcTime: 30 * 60 * 1000, // 30 minut w cache
    refetchOnWindowFocus: false, // Nie odświeżaj automatycznie przy focus
    refetchOnMount: true, // Zawsze odśwież przy mount komponentu
    enabled: true, // Explicitly enable the query
  });
};

/**
 * Hook do pobierania plików powiązanych z konkretnym obiektem (LEAD, TASK, etc.)
 */
export const useFilesByRelatedObject = (relatedObjectId: number | null, relatedObjectType: 'LEAD' | 'TASK') => {
  return useMinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType(
    { id: relatedObjectId?.toString() || '', type: relatedObjectType }, 
    [], 
    {
      staleTime: 0,
      gcTime: 5 * 60 * 1000, // 5 minut w cache
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      enabled: !!relatedObjectId && !!relatedObjectType,
      refetchOnReconnect: true,
      retry: false, // WYŁĄCZ retry - nie ponawiaj żadnych zapytań
    }
  );
};

/**
 * Hook do zarządzania danymi plików z automatycznym cache'owaniem i aktualizacją
 */
export const useFileData = () => {
  const queryClient = useQueryClient();
  const filesQuery = useAllFiles();

  // Zmapowane dane
  const files = filesQuery.data || [];

  // Funkcja do odświeżania danych plików
  const refreshFiles = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['MinIoFileStorageServiceGetApiFilesMyFiles'] }),
      queryClient.invalidateQueries({ queryKey: ['MinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType'] })
    ]);
  };

  // Funkcja do aktualizacji pliku w cache bez ponownego pobierania
  const updateFileInCache = (fileId: number, updates: Partial<FileMetadataDTO>) => {
    // Aktualizuj cache dla wszystkich plików
    queryClient.setQueryData(['MinIoFileStorageServiceGetApiFilesMyFiles'], (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.map((file: any) => 
        file.id === fileId ? { ...file, ...updates } : file
      );
    });

    // Aktualizuj cache dla plików powiązanych z obiektami
    queryClient.invalidateQueries({ 
      queryKey: ['MinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType'] 
    });
  };

  // Funkcja do dodania pliku do cache
  const addFileToCache = (newFile: FileMetadataDTO) => {
    queryClient.setQueryData(['MinIoFileStorageServiceGetApiFilesMyFiles'], (oldData: any) => {
      if (!oldData) return [newFile];
      return [newFile, ...oldData];
    });

    // Odśwież cache dla plików powiązanych z obiektami
    queryClient.invalidateQueries({ 
      queryKey: ['MinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType'] 
    });
  };

  // Funkcja do usunięcia pliku z cache
  const removeFileFromCache = (filename: string) => {
    // Usuń z cache wszystkich plików
    queryClient.setQueryData(['MinIoFileStorageServiceGetApiFilesMyFiles'], (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.filter((file: any) => file.filename !== filename);
    });

    // Odśwież cache dla plików powiązanych z obiektami
    queryClient.invalidateQueries({ 
      queryKey: ['MinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType'] 
    });
  };

  // Funkcja do filtrowania plików według powiązanego obiektu
  const getFilesByRelatedObject = (relatedObjectId: string, relatedObjectType: 'LEAD' | 'TASK') => {
    return files.filter((file: FileMetadataDTO) => 
      file.relatedObjectId === relatedObjectId && 
      file.relatedObjectType === relatedObjectType
    );
  };

  // Funkcja do filtrowania plików według statusu
  const getFilesByStatus = (status?: 'ACTIVE' | 'DETACHED') => {
    if (!status) return files;
    return files.filter((file: FileMetadataDTO) => 
      status === 'ACTIVE' ? (file.status === 'ACTIVE' || !file.status) : file.status === status
    );
  };

  return {
    // Dane
    files,

    // Stany ładowania
    isLoading: filesQuery.isLoading,

    // Stany błędów
    error: filesQuery.error,

    // Funkcje pomocnicze
    refreshFiles,
    updateFileInCache,
    addFileToCache,
    removeFileFromCache,
    getFilesByRelatedObject,
    getFilesByStatus,

    // Oryginalne query object dla zaawansowanego użycia
    filesQuery
  };
};

/**
 * Hook do mutacji plików z automatyczną aktualizacją cache
 */
export const useFileMutations = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Funkcja do aktualizacji pliku w cache bez ponownego pobierania
  const updateFileInCache = (fileId: number, updates: Partial<FileMetadataDTO>) => {
    // Aktualizuj cache dla wszystkich plików
    queryClient.setQueryData(['MinIoFileStorageServiceGetApiFilesMyFiles'], (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.map((file: any) => 
        file.id === fileId ? { ...file, ...updates } : file
      );
    });

    // Aktualizuj cache dla plików powiązanych z obiektami
    queryClient.invalidateQueries({ 
      queryKey: ['MinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType'] 
    });
  };

  // Funkcja do dodania pliku do cache
  const addFileToCache = (newFile: FileMetadataDTO) => {
    queryClient.setQueryData(['MinIoFileStorageServiceGetApiFilesMyFiles'], (oldData: any) => {
      if (!oldData) return [newFile];
      return [newFile, ...oldData];
    });

    // Odśwież cache dla plików powiązanych z obiektami
    queryClient.invalidateQueries({ 
      queryKey: ['MinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType'] 
    });
  };

  // Funkcja do usunięcia pliku z cache
  const removeFileFromCache = (filename: string) => {
    // Usuń z cache wszystkich plików
    queryClient.setQueryData(['MinIoFileStorageServiceGetApiFilesMyFiles'], (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.filter((file: any) => file.filename !== filename);
    });

    // Odśwież cache dla głównej listy plików
    queryClient.invalidateQueries({ 
      queryKey: ['MinIoFileStorageServiceGetApiFilesMyFiles'] 
    });

    // Odśwież cache dla plików powiązanych z obiektami
    queryClient.invalidateQueries({ 
      queryKey: ['MinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType'] 
    });
  };

  // Mutation do przesyłania pliku
  const uploadFileMutation = useMinIoFileStorageServicePostApiFilesUpload({
    onSuccess: (data) => {
      addFileToCache(data);
      enqueueSnackbar('Plik został pomyślnie przesłany', { variant: 'success' });
    },
    onError: (error) => {
      console.error('Błąd podczas przesyłania pliku:', error);
      const errorMessage = error instanceof Error ? error.message : 'Nieznany błąd';
      enqueueSnackbar(`Błąd podczas przesyłania pliku: ${errorMessage}`, { variant: 'error' });
    }
  });

  // Mutation do trwałego usuwania pliku (tylko dla plików DETACHED)
  const permanentDeleteFileMutation = useMinIoFileStorageServiceDeleteApiFilesByFilename({
    onSuccess: (data, variables) => {
      removeFileFromCache(variables.filename);
      enqueueSnackbar('Plik został trwale usunięty', { variant: 'success' });
    },
    onError: (error) => {
      console.error('Błąd podczas trwałego usuwania pliku:', error);
      const errorMessage = error instanceof Error ? error.message : 'Nieznany błąd';
      enqueueSnackbar(`Błąd podczas trwałego usuwania pliku: ${errorMessage}`, { variant: 'error' });
    }
  });

  // Mutation do zmiany statusu pliku na backendzie
  const changeFileStatusMutation = useMinIoFileStorageServicePatchApiFilesByFilenameStatus({
    onSuccess: (data, variables) => {
      // Po pomyślnej zmianie statusu na backendzie, znajdź plik w cache i zaktualizuj go
      queryClient.setQueryData(['MinIoFileStorageServiceGetApiFilesMyFiles'], (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.map((file: any) => 
          file.filename === variables.filename ? { ...file, status: variables.status } : file
        );
      });

      // Odśwież cache dla plików powiązanych z obiektami
      queryClient.invalidateQueries({ 
        queryKey: ['MinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType'] 
      });

      const statusText = variables.status === 'DETACHED' ? 'przeniesiony do kosza' : 'przywrócony z kosza';
      enqueueSnackbar(`Plik został ${statusText}`, { variant: 'success' });
    },
    onError: (error, variables) => {
      console.error('Błąd podczas zmiany statusu pliku:', error);
      const errorMessage = error instanceof Error ? error.message : 'Nieznany błąd';
      const actionText = variables.status === 'DETACHED' ? 'przenoszenia do kosza' : 'przywracania z kosza';
      enqueueSnackbar(`Błąd podczas ${actionText}: ${errorMessage}`, { variant: 'error' });
    }
  });

  // Mutation do zmiany statusu wielu plików naraz
  const bulkChangeFileStatusMutation = useMinIoFileStorageServicePatchApiFilesBulkStatus({
    onSuccess: (data, variables) => {
      // Po pomyślnej zmianie statusu na backendzie, zaktualizuj pliki w cache
      queryClient.setQueryData(['MinIoFileStorageServiceGetApiFilesMyFiles'], (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.map((file: any) => 
          variables.requestBody.includes(file.filename) 
            ? { ...file, status: variables.status } 
            : file
        );
      });

      // Odśwież cache dla plików powiązanych z obiektami
      queryClient.invalidateQueries({ 
        queryKey: ['MinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType'] 
      });

      const statusText = variables.status === 'DETACHED' ? 'przeniesione do kosza' : 'przywrócone z kosza';
      const fileCount = variables.requestBody.length;
      enqueueSnackbar(`${fileCount} ${fileCount === 1 ? 'plik został' : 'plików zostało'} ${statusText}`, { variant: 'success' });
    },
    onError: (error, variables) => {
      console.error('Błąd podczas zmiany statusu plików:', error);
      const errorMessage = error instanceof Error ? error.message : 'Nieznany błąd';
      const actionText = variables.status === 'DETACHED' ? 'przenoszenia do kosza' : 'przywracania z kosza';
      enqueueSnackbar(`Błąd podczas ${actionText} plików: ${errorMessage}`, { variant: 'error' });
    }
  });

  // Mutation do trwałego usuwania wielu plików naraz
  const bulkDeleteFilesMutation = useMinIoFileStorageServiceDeleteApiFilesBulk({
    onSuccess: (data, variables) => {
      // Usuń pliki z cache
      queryClient.setQueryData(['MinIoFileStorageServiceGetApiFilesMyFiles'], (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.filter((file: any) => !variables.requestBody.includes(file.filename));
      });

      // Odśwież cache dla plików powiązanych z obiektami
      queryClient.invalidateQueries({ 
        queryKey: ['MinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType'] 
      });

      const fileCount = variables.requestBody.length;
      enqueueSnackbar(`${fileCount} ${fileCount === 1 ? 'plik został' : 'plików zostało'} trwale usuniętych`, { variant: 'success' });
    },
    onError: (error, variables) => {
      console.error('Błąd podczas trwałego usuwania plików:', error);
      const errorMessage = error instanceof Error ? error.message : 'Nieznany błąd';
      enqueueSnackbar(`Błąd podczas trwałego usuwania plików: ${errorMessage}`, { variant: 'error' });
    }
  });

  // Funkcja do obsługi usuwania pliku z uwzględnieniem statusu
  const handleFileDelete = (file: FileMetadataDTO) => {
    if (!file.filename) {
      enqueueSnackbar('Brak nazwy pliku', { variant: 'error' });
      return;
    }

    const currentStatus = file.status || 'ACTIVE'; // Domyślnie ACTIVE jeśli brak statusu

    if (currentStatus === 'DETACHED') {
      // Plik jest już w koszu - usuń trwale
      permanentDeleteFileMutation.mutate({ filename: file.filename });
    } else {
      // Plik jest aktywny - przenieś do kosza (zmień status na DETACHED na backendzie)
      changeFileStatusMutation.mutate({ 
        filename: file.filename, 
        status: 'DETACHED' 
      });
    }
  };

  // Funkcja do przywracania pliku z kosza (zmiana statusu z DETACHED na ACTIVE)
  const handleFileRestore = (file: FileMetadataDTO) => {
    if (!file.filename) {
      enqueueSnackbar('Brak nazwy pliku', { variant: 'error' });
      return;
    }

    const currentStatus = file.status || 'ACTIVE';

    if (currentStatus !== 'DETACHED') {
      enqueueSnackbar('Można przywrócić tylko pliki z kosza', { variant: 'warning' });
      return;
    }

    // Zmień status z DETACHED na ACTIVE na backendzie
    changeFileStatusMutation.mutate({ 
      filename: file.filename, 
      status: 'ACTIVE' 
    });
  };

  // Funkcja do pobierania pliku (download)
  const downloadFile = async (filename: string, originalFilename?: string) => {
    try {
      const response = await MinIoFileStorageService.getApiFilesByFilenameDownload({ filename });

      // The response contains downloadUrl
      const downloadUrl = (response as any).downloadUrl;
      if (!downloadUrl) {
        throw new Error('No download URL in response');
      }

      // Fetch the actual file using the download URL
      const fileResponse = await fetch(downloadUrl);
      if (!fileResponse.ok) {
        throw new Error(`HTTP error! status: ${fileResponse.status}`);
      }

      const blob = await fileResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalFilename || filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Clean up the blob URL
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);

      enqueueSnackbar('Plik został pobrany', { variant: 'success' });
    } catch (error) {
      console.error('Błąd podczas pobierania pliku:', error);
      const errorMessage = error instanceof Error ? error.message : 'Nieznany błąd';
      enqueueSnackbar(`Błąd podczas pobierania pliku: ${errorMessage}`, { variant: 'error' });
    }
  };

  // Funkcja do obsługi masowego przenoszenia plików do kosza lub przywracania
  const handleBulkStatusChange = (filenames: string[], status: 'ACTIVE' | 'DETACHED') => {
    if (filenames.length === 0) {
      enqueueSnackbar('Nie wybrano żadnych plików', { variant: 'warning' });
      return;
    }

    bulkChangeFileStatusMutation.mutate({
      requestBody: filenames,
      status: status
    });
  };

  // Funkcja do obsługi masowego trwałego usuwania plików
  const handleBulkDelete = (filenames: string[]) => {
    if (filenames.length === 0) {
      enqueueSnackbar('Nie wybrano żadnych plików', { variant: 'warning' });
      return;
    }

    bulkDeleteFilesMutation.mutate({
      requestBody: filenames
    });
  };

  return {
    // Funkcje mutacji
    uploadFile: uploadFileMutation.mutateAsync,
    deleteFile: handleFileDelete,
    restoreFile: handleFileRestore,
    downloadFile,
    bulkStatusChange: handleBulkStatusChange,
    bulkDelete: handleBulkDelete,

    // Stany mutacji
    isUploading: uploadFileMutation.isPending,
    isDeleting: permanentDeleteFileMutation.isPending,
    isChangingStatus: changeFileStatusMutation.isPending,
    isBulkChangingStatus: bulkChangeFileStatusMutation.isPending,
    isBulkDeleting: bulkDeleteFilesMutation.isPending,

    // Funkcje pomocnicze cache
    updateFileInCache,
    addFileToCache,
    removeFileFromCache,
  };
};

/**
 * Hook do pobierania plików powiązanych z konkretnym obiektem
 */
export const useRelatedFiles = (relatedObjectId: number | null, relatedObjectType: 'LEAD' | 'TASK') => {
  const filesQuery = useFilesByRelatedObject(relatedObjectId, relatedObjectType);

  return {
    files: filesQuery.data || [],
    isLoading: filesQuery.isLoading,
    error: filesQuery.error,
    refetch: filesQuery.refetch,
  };
};
